import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { ANALYTICS_PARAMS } from "@/features/analytics/models";
import { IS_CALL_CENTER_PARAM_KEY } from "@/features/onboarding/models";
import { useOnboardingStore } from "@/features/onboarding/stores";
import {
  getAttributionCookie,
  getCookie,
  setAttributionCookie,
} from "@/shared/lib/attribution-cookie";

const hasEverflowClickInSession = (offerId: string, affiliateId: string) =>
  getCookie(`ef_session_${offerId}`) !== null &&
  getCookie("ef_affid") === affiliateId;

export const useEverflowClick = () => {
  const [searchParams] = useSearchParams();
  const setEfTransactionId = useOnboardingStore(
    (state) => state.setEfTransactionId,
  );

  useEffect(() => {
    const affid = searchParams.get(ANALYTICS_PARAMS.EVERFLOW_AFFILIATE_ID);
    const oid = searchParams.get(ANALYTICS_PARAMS.EVERFLOW_ORDER_ID);

    if (!affid || !oid) {
      return;
    }
    if (!window.EF) {
      return;
    }

    const urlTransactionIdParam = searchParams.get(
      ANALYTICS_PARAMS.EF_TRANSACTION_ID,
    );
    const urlTransactionId =
      urlTransactionIdParam === null || urlTransactionIdParam === ""
        ? undefined
        : urlTransactionIdParam;
    const existingTransactionId = getAttributionCookie()?._ef_transaction_id;
    const isStoredTransactionId =
      urlTransactionId !== undefined &&
      existingTransactionId === urlTransactionId;

    const isCallCenterLink =
      searchParams.get(IS_CALL_CENTER_PARAM_KEY) === "true";

    if (
      !isCallCenterLink &&
      (isStoredTransactionId || hasEverflowClickInSession(oid, affid))
    ) {
      const transactionId = urlTransactionId ?? window.EF.getTransactionId(oid);
      if (transactionId) {
        setEfTransactionId(transactionId);
        setAttributionCookie({
          ...(getAttributionCookie() ?? {}),
          [ANALYTICS_PARAMS.EF_TRANSACTION_ID]: transactionId,
        });
        return;
      }
    }

    void window.EF.click({
      offer_id: oid,
      affiliate_id: affid,
      transaction_id: urlTransactionId,
      source_id:
        searchParams.get(ANALYTICS_PARAMS.EVERFLOW_SOURCE_ID) ?? undefined,
      uid: searchParams.get(ANALYTICS_PARAMS.EVERFLOW_UID) ?? undefined,
      sub1: searchParams.get(ANALYTICS_PARAMS.EVERFLOW_SUB1) ?? undefined,
      sub2: searchParams.get(ANALYTICS_PARAMS.EVERFLOW_SUB2) ?? undefined,
      sub3: searchParams.get(ANALYTICS_PARAMS.EVERFLOW_SUB3) ?? undefined,
      sub4: searchParams.get(ANALYTICS_PARAMS.EVERFLOW_SUB4) ?? undefined,
      sub5: searchParams.get(ANALYTICS_PARAMS.EVERFLOW_SUB5) ?? undefined,
    }).then((transactionId) => {
      if (!transactionId) {
        return;
      }

      setEfTransactionId(transactionId);

      const existingCookie = getAttributionCookie();
      setAttributionCookie({
        ...(existingCookie ?? {}),
        [ANALYTICS_PARAMS.EF_TRANSACTION_ID]: transactionId,
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
